<template>
	<CardGeneric class="text-left max-sm:pb-4 billingInfoComponent">
		<p class="cardTitle">{{ $t("billingInformationTitle") }}</p>
		<p class="cardSubTitle">{{ $t("billingInformationSubtitle") }}</p>

		<div class="grid grid-cols-2 gap-y-5 gap-x-7">
			<FormCheckboxWithLabel
				id="sameBillingAndShipping"
				v-if="enableAdvancedShippingInfoFields"
				:label="$t('sameBillingAndShipping')"
				required
				class="col-span-2"
				:initialValue="billingAndShippingAreSame"
				@changed="handleSameBillingAndShipping" />

			<FormTextField
				id="billingFirstName"
				v-if="!billingAndShippingAreSame"
				:label="$t('billingFirstName')"
				class="max-sm:col-span-2"
				required
				:has-error="!!firstNameInvalid"
				:error-message="$t('invalidFirstName')"
				:disabled="shouldDisableCheckout"
				:initialValue="billingFirstName"
				:key="billingFirstName"
				@blur="
					({ value }) => {
						cartStore.setBillingFirstName(value)
						validateFirstName()
					}
				" />

			<FormTextField
				id="billingLastName"
				v-if="!billingAndShippingAreSame"
				:label="$t('billingLastName')"
				class="max-sm:col-span-2"
				required
				:has-error="!!lastNameInvalid"
				:error-message="$t('invalidLastName')"
				:disabled="shouldDisableCheckout"
				:initialValue="billingLastName"
				:key="billingLastName"
				@blur="
					({ value }) => {
						cartStore.setBillingLastName(value)
						validateLastName()
					}
				" />

			<FormTextField
				id="billingAddress1"
				v-if="!billingAndShippingAreSame"
				:label="$t('shippingAddress1')"
				class="col-span-2"
				required
				:has-error="!!address1Invalid"
				:error-message="$t('invalidAddress1')"
				:disabled="shouldDisableCheckout"
				:initialValue="billingAddress1"
				:key="billingAddress1"
				@blur="
					({ value }) => {
						cartStore.setBillingAddress1(value)
						validateAddress1()
					}
				" />

			<FormTextField
				id="billingAddress2"
				v-if="!billingAndShippingAreSame"
				:label="$t('shippingAddress2')"
				class="col-span-2"
				:disabled="shouldDisableCheckout"
				:initialValue="billingAddress2"
				:key="billingAddress2"
				@blur="({ value }) => cartStore.setBillingAddress2(value)" />

			<FormDropdown
				id="billingCountry"
				v-if="!billingAndShippingAreSame"
				:label="$t('shippingCountry')"
				class="max-sm:col-span-2"
				required
				:has-error="!!countryInvalid"
				:error-message="$t('invalidCountry')"
				:disabled="shouldDisableCheckout"
				:options="countries"
				:initialValue="billingCountry"
				:key="billingCountry"
				@blur="validateCountry"
				@changed="handleCountryChanged" />

			<FormDropdown
				id="billingState"
				v-if="!billingAndShippingAreSame"
				:label="billingCountry === 'CAN' ? $t('shippingProvince') : $t('shippingState')"
				class="max-sm:col-span-2"
				:required="states.length > 0"
				:has-error="!!stateInvalid"
				:error-message="billingCountry === 'CAN' ? $t('invalidProvince') : $t('invalidState')"
				:disabled="shouldDisableCheckout || !billingCountry || states.length <= 0"
				:options="states"
				:initialValue="billingState"
				:key="billingCountry + billingState"
				@blur="validateState"
				@changed="
					({ value }) => {
						cartStore.setBillingState(value)
						validateState()
					}
				" />

			<FormTextField
				id="billingCity"
				v-if="!billingAndShippingAreSame"
				:label="$t('shippingCity')"
				class="max-sm:col-span-2"
				required
				:has-error="!!cityInvalid"
				:error-message="$t('invalidCity')"
				:disabled="shouldDisableCheckout"
				:initialValue="billingCity"
				:key="billingCity"
				@blur="
					({ value }) => {
						cartStore.setBillingCity(value)
						validateCity()
					}
				" />

			<FormZipCodeField
				id="billingZipcode"
				v-if="!billingAndShippingAreSame"
				:label="billingCountry === 'CAN' ? $t('shippingPostalCode') : $t('shippingZipcode')"
				class="max-sm:col-span-2"
				required
				:has-error="!!zipcodeInvalid"
				:error-message="billingCountry === 'CAN' ? $t('invalidPostalCode') : $t('invalidZipcode')"
				:disabled="shouldDisableCheckout"
				:initialValue="billingZipcode"
				:country="billingCountry"
				:key="billingCountry + billingZipcode"
				@blur="
					({ value }) => {
						cartStore.setBillingZipcode(value)
						validateZipCode()
					}
				"
				ref="billingZipcodeRef" />
		</div>
	</CardGeneric>
</template>

<script setup>
	import { useCartStore } from "~/stores/CartStore"
	import { useCurrencyStore } from "~/stores/CurrencyStore"
	import { isNonEmpty } from "~/assets/helpers/fieldValidators"
	import { UPDATE_BILLING_COUNTRY } from "assets/events/cart.js"

	const { $cartService, $countryAndStatesService, $currency, $eventBus, $configs } = useNuxtApp()
	const currencyStore = useCurrencyStore()
	const { currency } = storeToRefs(currencyStore)

	const runtimeConfig = useRuntimeConfig()
	const isCheckoutDisabled = runtimeConfig.public.IS_CHECKOUT_DISABLED
	const shouldDisableCheckout = $configs.$isConfigEnabled(isCheckoutDisabled)
	const enableAdvancedShippingInfoFields = Boolean(runtimeConfig.public.ENABLE_DETAILED_SHIPPING_INFO)

	const cartStore = useCartStore()
	const {
		billingAndShippingAreSame,
		shippingFirstName,
		shippingLastName,
		shippingAddress1,
		shippingAddress2,
		shippingCountry,
		shippingState,
		shippingCity,
		shippingZipcode,
		billingFirstName,
		billingLastName,
		billingAddress1,
		billingAddress2,
		billingCountry,
		billingState,
		billingCity,
		billingZipcode,
	} = storeToRefs(cartStore)

	const firstNameInvalid = ref(false)
	const lastNameInvalid = ref(false)
	const address1Invalid = ref(false)
	const countryInvalid = ref(false)
	const stateInvalid = ref(false)
	const cityInvalid = ref(false)
	const zipcodeInvalid = ref(false)
	const billingZipcodeRef = ref(null)

	const handleSameBillingAndShipping = ({ value }) => {
		cartStore.setBillingAndShippingAreSame(value)

		if (value) {
			cartStore.setBillingFirstName(shippingFirstName.value)
			cartStore.setBillingLastName(shippingLastName.value)
			cartStore.setBillingAddress1(shippingAddress1.value)
			cartStore.setBillingAddress2(shippingAddress2.value)
			cartStore.setBillingCountry(shippingCountry.value)
			cartStore.setBillingState(shippingState.value)
			cartStore.setBillingCity(shippingCity.value)
			cartStore.setBillingZipcode(shippingZipcode.value)
		} else {
			cartStore.setBillingFirstName("")
			cartStore.setBillingLastName("")
			cartStore.setBillingAddress1("")
			cartStore.setBillingAddress2("")
			cartStore.setBillingCountry("")
			cartStore.setBillingState("")
			cartStore.setBillingCity("")
			cartStore.setBillingZipcode("")
		}
	}

	const states = ref([])
	const countries = $countryAndStatesService.$getSupportedCountries()
	const desiredCountryToSwitchTo = ref(null)

	const handleCountryChanged = ({ value }) => {
		// Attempt to update the currency before allowing the user to switch their country, if the currency call fails, then the billing country should not change.
		// This will prevent users from having to pay an exchange rate on their cards. Particularly noticeable in poor network conditions.
		const iso3 = value
		desiredCountryToSwitchTo.value = iso3

		const currency = $currency.$getCurrencyByCountryIso3(iso3)
		$cartService.$updateCartCurrency(currency.code)

		//Look for UPDATE_BILLING_COUNTRY event handler
	}

	//Update the available states if the billingCountry changed
	watch(billingCountry, (value) => {
		if (value) {
			states.value = $countryAndStatesService.$getStatesForCountryByIsoCode(value)
		}
	})

	//Populate the states for the billing country when the page is loaded
	//The Billing country could change if the user selects USD as the currency
	if (billingCountry.value) {
		states.value = $countryAndStatesService.$getStatesForCountryByIsoCode(billingCountry.value)
	}

	const validateFirstName = () => {
		firstNameInvalid.value = !isNonEmpty(billingFirstName.value)
	}

	const validateLastName = () => {
		lastNameInvalid.value = !isNonEmpty(billingLastName.value)
	}

	const validateAddress1 = () => {
		address1Invalid.value = !isNonEmpty(billingAddress1.value)
	}

	const validateCountry = () => {
		countryInvalid.value = !isNonEmpty(billingCountry.value)
	}

	const validateState = () => {
		stateInvalid.value = states.value.length > 0 ? !isNonEmpty(billingState.value) : false
	}

	const validateCity = () => {
		cityInvalid.value = !isNonEmpty(billingCity.value)
	}

	const validateZipCode = () => {
		zipcodeInvalid.value = !billingZipcodeRef.value?.isFieldValid()
	}

	//Called when the user clicks checkout (checkout.vue)
	const isBillingInfoValid = () => {
		if (billingAndShippingAreSame.value) {
			return true
		} else {
			validateFirstName()
			validateLastName()
			validateAddress1()
			validateCountry()
			validateState()
			validateCity()
			validateZipCode()

			return (
				!firstNameInvalid.value &&
				!lastNameInvalid.value &&
				!address1Invalid.value &&
				!countryInvalid.value &&
				!stateInvalid.value &&
				!cityInvalid.value &&
				!zipcodeInvalid.value
			)
		}
	}

	onMounted(() => {
		$eventBus.$on(UPDATE_BILLING_COUNTRY, () => {
			if (desiredCountryToSwitchTo.value) {
				countryInvalid.value = !isNonEmpty(desiredCountryToSwitchTo.value)
				stateInvalid.value = false

				cartStore.setBillingCountry(desiredCountryToSwitchTo.value)
				cartStore.setBillingState("")
				cartStore.setBillingZipcode("")
				states.value = $countryAndStatesService.$getStatesForCountryByIsoCode(desiredCountryToSwitchTo.value)

				//set desiredCountryToSwitchTo to null, otherwise a currency switch will trigger this method
				desiredCountryToSwitchTo.value = null
			}
		})
	})

	onUnmounted(() => {
		$eventBus.$off(UPDATE_BILLING_COUNTRY)
	})

	defineExpose({
		isBillingInfoValid,
	})
</script>

<style scoped></style>
